import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import {
  AP_COURSES,
  getApCourse,
  isAvailableApCourse,
  MAX_AP_COURSES_PER_STUDENT,
} from '../lib/apCourses';
import { usePublicSession } from './PublicSessionContext';
import { isLocalPreviewMode } from '../lib/localPreview';

const STORAGE_KEY = 'ap-platform-course-state-v1';
const ApCourseContext = createContext(null);

function readStoredState() {
  if (typeof window === 'undefined') return { selectedSlugs: [], activeSlug: null };
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    const selectedSlugs = Array.isArray(parsed.selectedSlugs)
      ? parsed.selectedSlugs.filter(isAvailableApCourse)
      : [];
    const activeSlug = selectedSlugs.includes(parsed.activeSlug) ? parsed.activeSlug : null;
    return { selectedSlugs, activeSlug };
  } catch (_error) {
    return { selectedSlugs: [], activeSlug: null };
  }
}

export function ApCourseProvider({ children }) {
  const { user } = usePublicSession();
  const initial = useMemo(readStoredState, []);
  const [selectedSlugs, setSelectedSlugs] = useState(initial.selectedSlugs);
  const [activeSlug, setActiveSlugState] = useState(initial.activeSlug);
  const [loading, setLoading] = useState(Boolean(user?.id && !isLocalPreviewMode()));

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ selectedSlugs, activeSlug }));
  }, [activeSlug, selectedSlugs]);

  useEffect(() => {
    if (!user?.id || !isSupabaseConfigured || isLocalPreviewMode()) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    void supabase
      .from('student_course_profiles')
      .select('course_slug,is_active,created_at')
      .eq('user_id', user.id)
      .order('created_at', { ascending: true })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data?.length) {
          const slugs = data.map((row) => row.course_slug).filter(isAvailableApCourse);
          setSelectedSlugs(slugs);
          setActiveSlugState(data.find((row) => row.is_active && slugs.includes(row.course_slug))?.course_slug || slugs[0] || null);
        }
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [user?.id]);

  const persistCourses = useCallback(async (nextSlugs, nextActiveSlug) => {
    if (!user?.id || !isSupabaseConfigured || isLocalPreviewMode()) return;
    const { error } = await supabase.rpc('set_ap_student_courses', {
      p_course_slugs: nextSlugs,
      p_active_slug: nextActiveSlug,
    });
    if (error) throw error;
  }, [user?.id]);

  const saveCourses = useCallback(async (slugs, requestedActiveSlug = null) => {
    const nextSlugs = [...new Set(slugs)].filter(isAvailableApCourse);
    if (nextSlugs.length > MAX_AP_COURSES_PER_STUDENT) {
      throw new Error(`Choose no more than ${MAX_AP_COURSES_PER_STUDENT} AP subjects.`);
    }
    const nextActiveSlug = nextSlugs.includes(requestedActiveSlug)
      ? requestedActiveSlug
      : nextSlugs[0] || null;
    const previousSlugs = selectedSlugs;
    const previousActiveSlug = activeSlug;
    setSelectedSlugs(nextSlugs);
    setActiveSlugState(nextActiveSlug);
    try {
      await persistCourses(nextSlugs, nextActiveSlug);
      return nextActiveSlug;
    } catch (error) {
      setSelectedSlugs(previousSlugs);
      setActiveSlugState(previousActiveSlug);
      throw error;
    }
  }, [activeSlug, persistCourses, selectedSlugs]);

  const setActiveSlug = useCallback(async (slug) => {
    if (!selectedSlugs.includes(slug)) return;
    const previousActiveSlug = activeSlug;
    setActiveSlugState(slug);
    try {
      await persistCourses(selectedSlugs, slug);
    } catch (error) {
      setActiveSlugState(previousActiveSlug);
      throw error;
    }
  }, [activeSlug, persistCourses, selectedSlugs]);

  const selectedCourses = useMemo(
    () => selectedSlugs.map(getApCourse).filter(Boolean),
    [selectedSlugs],
  );
  const activeCourse = getApCourse(activeSlug);

  const value = useMemo(() => ({
    courses: AP_COURSES,
    selectedCourses,
    selectedSlugs,
    activeCourse,
    activeSlug,
    loading,
    saveCourses,
    setActiveSlug,
  }), [activeCourse, activeSlug, loading, saveCourses, selectedCourses, selectedSlugs, setActiveSlug]);

  return <ApCourseContext.Provider value={value}>{children}</ApCourseContext.Provider>;
}

export function useApCourses() {
  const context = useContext(ApCourseContext);
  if (!context) throw new Error('useApCourses must be used within ApCourseProvider');
  return context;
}
